import type {
  ChargeSummaryExtended,
  ChargesHistoryResponse,
  ChargingAgingResponse,
  UpcomingExpense,
} from '@yawi/types';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Calendar, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface FinanzasOverviewProps {
  summary: ChargeSummaryExtended | null;
  aging: ChargingAgingResponse | null;
  history: ChargesHistoryResponse | null;
  upcoming: UpcomingExpense[];
  loading?: boolean;
  /** Oculta la columna de próximos egresos cuando el rol no tiene permiso sobre Expense. */
  showExpenses?: boolean;
}

const AGING_COLORS: Record<string, string> = {
  '1-30': '#639922',
  '31-60': '#E5B800',
  '61-90': '#EF9F27',
  '90+': '#E24B4A',
};

function formatMXN(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toLocaleString('es-MX')}`;
}

function formatPeriodLabel(period: string): string {
  try {
    const label = new Date(`${period}-01T00:00:00`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return period;
  }
}

export function FinanzasOverview({ summary, aging, history, upcoming, loading, showExpenses = true }: FinanzasOverviewProps) {
  const navigate = useNavigate();
  const cobrado = summary?.amountPaid ?? 0;
  const pendiente = summary?.amountPending ?? 0;
  const total = cobrado + pendiente;
  const cobradoPct = total > 0 ? Math.round((cobrado / total) * 100) : 0;
  const unitsCollected = summary?.unitsCollected ?? 0;
  const unitsPending = summary?.unitsPending ?? 0;
  const periodLabel = summary ? formatPeriodLabel(summary.period) : '—';

  const histPoints = history?.points ?? [];
  const agingBuckets = aging?.buckets ?? [];
  // Sin cargos este mes ni saldos ni historial: un solo mensaje en vez de ceros por todos lados.
  const noCharges = !loading && total === 0 && (aging?.totalAmount ?? 0) === 0
    && histPoints.every((p) => p.expectedAmount === 0);

  return (
    <div className="bg-white dark:bg-surface-secondary rounded-xl border-[0.5px] border-[#E8E6F8] dark:border-theme-light p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-[15px] font-semibold text-[#1A1A18] dark:text-theme">
          Finanzas <span className="font-normal text-[#888780] dark:text-theme-tertiary">— {periodLabel}</span>
        </h4>
        <button
          onClick={() => navigate('/admin/finanzas')}
          className="text-[12px] text-[#7F77DD] dark:text-primary-400 font-medium hover:underline whitespace-nowrap"
        >
          Ver detalle
        </button>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${showExpenses ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {noCharges ? (
          <div className="md:col-span-2 bg-[#F0EFF9] dark:bg-theme-200/20 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-2 min-h-[180px]">
            <Receipt size={24} className="text-[#7F77DD]" />
            <p className="text-[14px] font-semibold text-[#1A1A18] dark:text-theme">No hay cargos generados en {periodLabel.toLowerCase()}</p>
            <p className="text-[12px] text-[#888780] dark:text-theme-tertiary max-w-sm">
              Genera las cuotas del mes para empezar a ver cobranza, pendientes y antigüedad de saldos.
            </p>
            <button
              onClick={() => navigate('/admin/finanzas')}
              className="mt-1 text-[12px] font-medium text-white bg-[#7F77DD] hover:bg-[#6B63C9] rounded-lg px-3 py-1.5"
            >
              Ir a cuotas
            </button>
          </div>
        ) : (
        <>
        {/* Col 1: cobrado / pendiente + antigüedad */}
        <div>
          {loading ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-16 rounded bg-theme-200/50" />
              <div className="h-2 rounded-full bg-theme-200/50" />
              <div className="h-20 rounded bg-theme-200/50" />
            </div>
          ) : (
            <>
              <div className="flex gap-8 mb-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#3C9F3C] dark:text-green-400">Cobrado</p>
                  <p className="text-[24px] font-semibold text-[#1A1A18] dark:text-theme leading-tight">{formatMXN(cobrado)}</p>
                  <p className="text-[11px] text-[#888780] dark:text-theme-tertiary">{total > 0 ? `${cobradoPct}% de ${formatMXN(total)}` : `${unitsCollected} unidades`}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#D64545] dark:text-red-400">Pendiente</p>
                  <p className="text-[24px] font-semibold text-[#1A1A18] dark:text-theme leading-tight">{formatMXN(pendiente)}</p>
                  <p className="text-[11px] text-[#888780] dark:text-theme-tertiary">{unitsPending} unidades</p>
                </div>
              </div>
              <div className="flex h-1.5 rounded-full overflow-hidden gap-0.5 mb-4">
                {total > 0 ? (
                  <>
                    <div className="bg-[#3C9F3C] rounded-l-full transition-all" style={{ width: `${cobradoPct}%` }} />
                    <div className="bg-[#D64545] rounded-r-full flex-1" />
                  </>
                ) : (
                  <div className="bg-[#F0EFF9] dark:bg-theme-light rounded-full flex-1" />
                )}
              </div>

              <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme mb-2">Antigüedad de saldos</p>
              <div className="space-y-2">
                {agingBuckets.length === 0 ? (
                  <p className="text-[11px] text-[#888780] dark:text-theme-tertiary">Sin datos de antigüedad</p>
                ) : (
                  agingBuckets.map((bucket) => {
                    const color = AGING_COLORS[bucket.bucket] ?? '#888780';
                    const agingTotal = aging?.totalAmount ?? 0;
                    const widthPct = agingTotal > 0 ? Math.max(2, Math.round((bucket.amount / agingTotal) * 100)) : 0;
                    return (
                      <div key={bucket.bucket} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-[11px] text-[#888780] dark:text-theme-secondary w-14 flex-shrink-0">{bucket.bucket} días</span>
                        <div className="flex-1 h-2 rounded-full bg-[#F0EFF9] dark:bg-theme overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${widthPct}%`, backgroundColor: color }} />
                        </div>
                        <span className="text-[11px] font-semibold text-[#1A1A18] dark:text-theme w-16 text-right flex-shrink-0">{formatMXN(bucket.amount)}</span>
                        <span className="text-[11px] text-[#888780] dark:text-theme-tertiary w-10 text-right flex-shrink-0">{bucket.unitsCount} un.</span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Col 2: histórico cobranza chart */}
        <div className="bg-[#F0EFF9] dark:bg-theme-200/20 rounded-xl p-3 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-medium text-[#6E6C66] dark:text-theme-tertiary">Histórico de cobranza — {histPoints.length === 1 ? 'último mes' : `últimos ${histPoints.length || 8} meses`}</p>
          </div>
          {loading ? (
            <div className="h-[140px] rounded-lg bg-white/50 dark:bg-theme-200/50 animate-pulse" />
          ) : histPoints.length === 0 ? (
            <div className="h-[140px] flex items-center justify-center">
              <p className="text-[13px] text-[#888780] dark:text-theme-tertiary">Sin historial</p>
            </div>
          ) : (
            <div className="h-[140px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={histPoints} barSize={18}>
                  <CartesianGrid vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 9, fill: '#888780' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(127,119,221,0.08)' }}
                    formatter={(v) => [`${v}%`, 'Cobranza']}
                    contentStyle={{ fontSize: 11, borderRadius: 8, backgroundColor: '#2C2C2A', border: 'none', color: '#FFFFFF' }}
                    itemStyle={{ color: '#FFFFFF' }}
                    labelStyle={{ color: '#FFFFFF' }}
                  />
                  <Bar dataKey="collectionPct" radius={[4, 4, 0, 0]}>
                    {histPoints.map((point, i) => (
                      <Cell key={i} fill={point.isCurrent ? '#7F77DD' : '#D7D4F2'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        </>
        )}

        {/* Col 3: próximos egresos */}
        {showExpenses && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Calendar size={14} className="text-[#888780]" />
              <p className="text-[13px] font-semibold text-[#1A1A18] dark:text-theme">Próximos egresos</p>
            </div>
            {loading ? (
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-[#F0EFF9] dark:bg-theme-200/50" />
                ))}
              </div>
            ) : upcoming.length === 0 ? (
              <p className="text-[13px] text-[#888780] dark:text-theme-tertiary">Sin egresos próximos</p>
            ) : (
              <div className="space-y-2">
                {upcoming.map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-white bg-[#7F77DD] rounded-lg px-1.5 py-0.5 flex-shrink-0 min-w-[26px] text-center">
                      {item.daysUntilDue}d
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#2C2C2A] dark:text-theme truncate leading-tight">{item.concept}</p>
                      {item.provider && (
                        <p className="text-[11px] text-[#888780] dark:text-theme-tertiary truncate leading-tight">{item.provider}</p>
                      )}
                    </div>
                    <span className="text-[13px] font-medium text-[#2C2C2A] dark:text-theme flex-shrink-0">{formatMXN(item.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => navigate('/admin/egresos')}
              className="mt-3 text-[12px] text-[#7F77DD] dark:text-primary-400 font-medium hover:underline"
            >
              Ver todos
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
